import type { Job } from "@studio/shared";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { PageHeader } from "@/components/AppShell";
import { EnhanceDock } from "@/components/EnhanceDock";
import { PromptDock, type PromptKind } from "@/components/PromptDock";
import { RunCard } from "@/components/RunCard";
import { api } from "@/lib/api";
import { useComposer } from "@/lib/composer";
import { useLive } from "@/lib/events";
import type { CreateKind } from "@/router";

const IDEAS: Record<PromptKind, { title: string; lines: string[] }> = {
  video: {
    title: "What should we film?",
    lines: [
      "A lighthouse at dusk as a storm rolls in, waves crash on the rocks, slow drone push-in, the roar of the sea",
      "A barista pours latte art in close-up, steam rising, café chatter and soft jazz in the background",
      "A golden retriever runs through autumn leaves toward the camera in slow motion, leaves crunching",
    ],
  },
  image: {
    title: "What should we picture?",
    lines: [
      "A cosy reading nook by a rainy window, warm lamp light, a cat asleep on a knitted blanket",
      "A birthday cake shaped like a castle on a pastel table, candles glowing, shallow depth of field",
      "A portrait of an old fisherman with a weathered face, soft north-facing window light, 85mm",
    ],
  },
  music: {
    title: "What should we play?",
    lines: [
      "Warm indie folk, acoustic guitar, brushed drums, soft female vocal, 90 BPM",
      "Upbeat birthday pop song, handclaps, bright synths, cheerful choir",
      "Lo-fi hip hop for studying, dusty vinyl drums, mellow Rhodes, rain ambience",
    ],
  },
  speech: {
    title: "What should we say?",
    lines: [
      "Thanks for listening. Next week we look at how a single graphics card makes a minute of video.",
      "Tonight: rain after midnight, clearing by morning, with a high of eleven degrees.",
      "Your table is ready. Follow me — we have kept the one by the window, as you asked.",
    ],
  },
};

function useJobsOfKind(kind: CreateKind): Job[] {
  const fetched = useQuery({ queryKey: ["jobs"], queryFn: () => api.jobs() });
  const upsert = useLive((s) => s.upsertJob);
  useEffect(() => {
    for (const job of fetched.data ?? []) upsert(job);
  }, [fetched.data, upsert]);
  const jobs = useLive((s) => s.jobs);
  return useMemo(
    () =>
      Object.values(jobs)
        .filter((job) => job.type === kind && job.status !== "cancelled")
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [jobs, kind],
  );
}

export function CreatePage() {
  const { kind } = useSearch({ from: "/create" });
  const jobs = useJobsOfKind(kind);
  const composer = useComposer();

  const fillIdea = (line: string) => {
    if (kind === "enhance") return;
    if (kind === "image") composer.setImage({ prompt: line });
    else if (kind === "video") composer.setVideo({ prompt: line });
    else if (kind === "music") composer.setMusic({ style: line });
    else composer.setSpeech({ text: line });
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Create" />
      <div id="run-feed" className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[880px] px-4 md:px-6">
          {jobs.length === 0 && kind === "enhance" ? (
            <section className="flex min-h-[46dvh] flex-col justify-end pb-6 pt-10">
              <h2 className="font-display text-[26px] leading-tight md:text-[34px]">
                What should look better?
              </h2>
              <p className="mt-2 max-w-[56ch] text-mute">
                Add a picture or a video you already have. It comes back restored and enlarged: sharper
                detail, cleaner edges, less noise. Pictures grow up to four times, videos up to 4K with their
                sound kept. The name of the model is a choice: a picture can instead be cleared of heavy
                noise, and a video turned into HDR. Every finished picture and video also has an Enhance
                button of its own.
              </p>
            </section>
          ) : jobs.length === 0 && kind !== "enhance" ? (
            <section className="flex min-h-[46dvh] flex-col justify-end pb-6 pt-10">
              <h2 className="font-display text-[26px] leading-tight md:text-[34px]">{IDEAS[kind].title}</h2>
              <p className="mt-2 max-w-[56ch] text-mute">
                {kind === "video"
                  ? "Describe a scene and get a short film with sound. Add a photo to bring it to life, or people and places to use as references."
                  : kind === "image"
                    ? "Describe what you want to see, or add a picture and draw on the region you want Qwen to change."
                    : kind === "music"
                      ? "Describe the sound and mood; add your own lyrics or let it sing."
                      : "Type the words. Pick one of the nine voices, clone one you saved, or describe a speaker."}
              </p>
              <div className="mt-5 flex flex-col gap-2">
                {IDEAS[kind].lines.map((line) => (
                  <button
                    key={line}
                    type="button"
                    onClick={() => fillIdea(line)}
                    className="rounded-[var(--radius-control)] border border-line/40 px-4 py-3 text-left text-[15px] text-paper/90 transition-colors hover:border-line hover:bg-panel"
                  >
                    {line}
                  </button>
                ))}
              </div>
            </section>
          ) : (
            <div className="pb-6 pt-2 md:pt-0">
              {jobs.map((job) => (
                <RunCard key={job.id} job={job} />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="shrink-0 bg-gradient-to-t from-desk via-desk to-desk/0 pt-2">
        {kind === "enhance" ? <EnhanceDock /> : <PromptDock kind={kind} />}
      </div>
    </div>
  );
}
